// src/components/EstateLicencesPanel.jsx
// Licences de reproduction d'un estate : qui peut reproduire quoi, où, jusqu'à quand, et où se trouve le document.
// Information saisie par le propriétaire : ARTICHAUT ne qualifie aucun droit et ne vérifie aucun contrat.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";

const STATUSES = ["requested", "active", "expired", "terminated"];
const EMPTY = { licensee: "", scope: "", territory: "", start_date: "", end_date: "", terms_note: "", document_note: "", status: "active" };

const TEXT = {
  fr: {
    title: "Licences de reproduction", add: "Ajouter une licence", save: "Enregistrer la licence", cancel: "Annuler", edit: "Modifier", del: "Supprimer", confirmDel: "Supprimer cette licence ?",
    empty: "Aucune licence enregistrée.", soon: "échéance proche", over: "échue",
    err: "Enregistrement impossible : ", errDates: "La fin ne peut pas précéder le début.", errName: "Indiquez le bénéficiaire.",
    f: { licensee: "Bénéficiaire", scope: "Étendue (catalogue, produits dérivés, presse, usage en ligne…)", territory: "Territoire", start_date: "Début", end_date: "Fin", terms_note: "Conditions (résumé : le contrat reste chez le juriste)", document_note: "Où se trouve le document", status: "Statut" },
    status: { requested: "Demandée", active: "Active", expired: "Expirée", terminated: "Terminée" },
  },
  en: {
    title: "Reproduction licences", add: "Add a licence", save: "Save the licence", cancel: "Cancel", edit: "Edit", del: "Delete", confirmDel: "Delete this licence?",
    empty: "No licence recorded.", soon: "expires soon", over: "expired",
    err: "Could not save: ", errDates: "The end cannot be before the start.", errName: "Enter the licensee.",
    f: { licensee: "Licensee", scope: "Scope (catalogue, merchandise, press, online use…)", territory: "Territory", start_date: "Start", end_date: "End", terms_note: "Terms (summary: the contract stays with the lawyer)", document_note: "Where the document is", status: "Status" },
    status: { requested: "Requested", active: "Active", expired: "Expired", terminated: "Terminated" },
  },
  de: {
    title: "Vervielfältigungslizenzen", add: "Lizenz hinzufügen", save: "Lizenz speichern", cancel: "Abbrechen", edit: "Bearbeiten", del: "Löschen", confirmDel: "Diese Lizenz löschen?",
    empty: "Keine Lizenz erfasst.", soon: "läuft bald ab", over: "abgelaufen",
    err: "Speichern nicht möglich: ", errDates: "Das Ende darf nicht vor dem Beginn liegen.", errName: "Geben Sie den Lizenznehmer an.",
    f: { licensee: "Lizenznehmer", scope: "Umfang (Katalog, Merchandising, Presse, Online-Nutzung …)", territory: "Gebiet", start_date: "Beginn", end_date: "Ende", terms_note: "Bedingungen (Zusammenfassung: der Vertrag bleibt beim Juristen)", document_note: "Wo das Dokument liegt", status: "Status" },
    status: { requested: "Angefragt", active: "Aktiv", expired: "Abgelaufen", terminated: "Beendet" },
  },
};

const box = { width: "100%", boxSizing: "border-box", padding: "7px 9px", border: "1px solid #e5e3df", borderRadius: 4, fontSize: 13, fontFamily: "system-ui, sans-serif", background: "#fff" };
const btn = (primary) => ({ padding: "6px 12px", borderRadius: 2, fontSize: 12.5, cursor: "pointer", border: "1px solid #1a1a1a", background: primary ? "#1a1a1a" : "#fff", color: primary ? "#fff" : "#1a1a1a", fontWeight: 600 });

export function licenceTiming(l, today = new Date()) {
  if (l?.status !== "active" || !l?.end_date) return null;
  const end = new Date(l.end_date + "T00:00:00");
  const days = Math.round((end - today) / 86400000);
  return days < 0 ? "over" : days <= 90 ? "soon" : null;
}

export default function EstateLicencesPanel({ collectionId }) {
  const { i18n } = useTranslation();
  const L = TEXT[String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2)] || TEXT.fr;
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null); // null = fermé ; objet = en édition
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const { data } = await supabase.from("estate_licences").select("*").eq("collection_id", collectionId).order("end_date", { ascending: true, nullsFirst: false });
    setRows(data || []);
  }, [collectionId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setMsg("");
    if (!String(form.licensee || "").trim()) { setMsg(L.err + L.errName); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { setMsg(L.err + L.errDates); return; }
    const row = { collection_id: collectionId, updated_at: new Date().toISOString() };
    Object.keys(EMPTY).forEach((k) => { const v = String(form[k] ?? "").trim(); row[k] = v === "" ? (k === "status" ? "active" : null) : v; });
    const q = form.id ? supabase.from("estate_licences").update(row).eq("id", form.id) : supabase.from("estate_licences").insert(row);
    const { error } = await q;
    if (error) { setMsg(L.err + error.message); return; }
    setForm(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm(L.confirmDel)) return;
    await supabase.from("estate_licences").delete().eq("id", id);
    load();
  };

  return (
    <div style={{ margin: "14px 0 0" }}>
      <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", fontWeight: 700, marginBottom: 8 }}>{L.title}</div>
      {rows.length === 0 && !form && <div style={{ fontSize: 12.5, color: "#8a8378" }}>{L.empty}</div>}
      <div style={{ display: "grid", gap: 8 }}>
        {rows.map((l) => {
          const timing = licenceTiming(l);
          return (
            <div key={l.id} style={{ border: "1px solid #e5e3df", borderRadius: 6, padding: "8px 10px", fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <strong>{l.licensee}</strong>
                <span style={{ fontSize: 12, color: timing ? "#b4572f" : "#6f695f", fontWeight: timing ? 700 : 400 }}>
                  {L.status[l.status] || l.status}{timing ? ` · ${timing === "over" ? L.over : L.soon}` : ""}
                </span>
              </div>
              <div style={{ color: "#6f695f", fontSize: 12.5 }}>
                {[l.scope, l.territory, [l.start_date, l.end_date].filter(Boolean).join(" → ")].filter(Boolean).join(" · ")}
              </div>
              {l.document_note && <div style={{ color: "#8a8378", fontSize: 12 }}>{l.document_note}</div>}
              <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                <button type="button" style={btn(false)} onClick={() => { setMsg(""); setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(l).map(([k, v]) => [k, v ?? ""])) }); }}>{L.edit}</button>
                <button type="button" style={{ ...btn(false), color: "#8f3a44", borderColor: "#8f3a44" }} onClick={() => remove(l.id)}>{L.del}</button>
              </div>
            </div>
          );
        })}
      </div>
      {form && (
        <div style={{ border: "1px solid #c9a961", borderRadius: 6, padding: 10, marginTop: 8, display: "grid", gap: 8 }}>
          {["licensee", "scope", "territory", "terms_note", "document_note"].map((k) => (
            <label key={k} style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
              <span style={{ fontWeight: 600 }}>{L.f[k]}</span>
              {k === "terms_note" ? <textarea style={{ ...box, minHeight: 56 }} value={form[k] || ""} maxLength={2000} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                : <input style={box} value={form[k] || ""} maxLength={k === "licensee" || k === "territory" ? 300 : 1000} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />}
            </label>
          ))}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {["start_date", "end_date"].map((k) => (
              <label key={k} style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
                <span style={{ fontWeight: 600 }}>{L.f[k]}</span>
                <input style={{ ...box, width: 170 }} type="date" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </label>
            ))}
            <label style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d" }}>
              <span style={{ fontWeight: 600 }}>{L.f.status}</span>
              <select style={{ ...box, width: 170 }} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.map((s) => <option key={s} value={s}>{L.status[s]}</option>)}
              </select>
            </label>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button type="button" style={btn(true)} onClick={save}>{L.save}</button>
            <button type="button" style={btn(false)} onClick={() => { setForm(null); setMsg(""); }}>{L.cancel}</button>
            {msg && <span style={{ fontSize: 12.5, color: "#8f3a44" }}>{msg}</span>}
          </div>
        </div>
      )}
      {!form && <div style={{ marginTop: 8 }}><button type="button" style={btn(false)} onClick={() => { setMsg(""); setForm({ ...EMPTY }); }}>{L.add}</button></div>}
    </div>
  );
}
